import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Tag, Power } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { SlidePanel } from '../../components/common/SlidePanel';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { MoneyInput } from '../../components/common/MoneyInput';
import { DatePicker } from '../../components/common/DatePicker';
import { Toast } from '../../components/common/Toast';
import { EmptyState } from '../../components/common/EmptyState';
import { canonicalMoney } from '../../lib/money';
import { SettingsSubPage } from '../Settings/components/SettingsSubPage';

interface PromoCode {
  id: string; business_id: string; code: string; description: string | null;
  discount_type: 'percentage' | 'fixed'; discount_value: number;
  min_order_amount: number | null; max_discount_amount: number | null;
  max_uses: number | null; max_uses_per_client: number; current_uses: number;
  starts_at: string; expires_at: string | null; is_active: boolean;
  applies_to: string | null; service_ids: string[] | null; new_clients_only: boolean;
  created_at: string;
}

const TH: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: admin.caption, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${admin.border}`, whiteSpace: 'nowrap' };
const TD: React.CSSProperties = { fontSize: 13, color: palette.g700, padding: '10px 12px', borderBottom: `1px solid ${palette.g100}`, whiteSpace: 'nowrap' };
const LABEL: React.CSSProperties = { fontSize: 13, fontWeight: 500, color: palette.g500, marginBottom: 4, display: 'block' };
const INPUT: React.CSSProperties = { width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${palette.g200}`, fontSize: 13.5, color: palette.g800, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' as const };

export function PromoCodesPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<PromoCode | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const mountedRef = useRef(true);

  // eslint-disable-next-line react-hooks/preserve-manual-memoization
  const fetchCodes = useCallback(async () => {
    if (!business?.id) return;
    const { data } = await supabase.from('promo_codes').select('*').eq('business_id', business.id).order('created_at', { ascending: false });
    if (mountedRef.current) { setCodes((data ?? []) as PromoCode[]); setLoading(false); }
  }, [business?.id]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { mountedRef.current = true; void fetchCodes(); return () => { mountedRef.current = false; }; }, [fetchCodes]);

  const statusOf = (c: PromoCode) => {
    if (!c.is_active) return { label: 'Inactive', bg: palette.g100, fg: palette.g500 };
    if (c.expires_at && new Date(c.expires_at) < new Date()) return { label: 'Expired', bg: palette.redWash, fg: palette.red };
    if (c.max_uses && c.current_uses >= c.max_uses) return { label: 'Maxed', bg: palette.amberWash, fg: palette.amber };
    return { label: 'Active', bg: palette.greenWash, fg: palette.green };
  };

  return (
    <SettingsSubPage title="Promo Codes" description="Create discount codes for clients to use during booking or checkout">
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowCreate(true)} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '7px 14px', borderRadius: 8, border: 'none', background: palette.accent, fontSize: 13, fontWeight: 600, color: palette.white, cursor: 'pointer', fontFamily: 'inherit' }}><Plus size={15} /> Create Code</button>
      </div>

      {!loading && codes.length === 0 ? (
        <EmptyState icon={<Tag size={22} />} title="No promo codes yet" subtitle="Create a discount code to share with clients." actionLabel="Create Code" onAction={() => setShowCreate(true)} />
      ) : (
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, boxShadow: shadow.card }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={TH}>Code</th><th style={TH}>Discount</th><th style={TH}>Uses</th><th style={TH}>Status</th><th style={TH}>Expires</th></tr></thead>
            <tbody>{codes.map(c => {
              const st = statusOf(c);
              return (
                <tr key={c.id} onClick={() => setEditing(c)} style={{ cursor: 'pointer', transition: `background ${transition.fast}` }} onMouseEnter={e => (e.currentTarget.style.background = palette.g50)} onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  <td style={{ ...TD, fontWeight: 600, color: palette.g800, fontFamily: 'monospace' }}>{c.code}</td>
                  <td style={TD}>{c.discount_type === 'percentage' ? `${c.discount_value}%` : `$${c.discount_value}`}{c.description ? ` — ${c.description}` : ''}</td>
                  <td style={TD}>{c.current_uses}{c.max_uses ? ` / ${c.max_uses}` : ''}</td>
                  <td style={TD}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500, background: st.bg, color: st.fg }}>{st.label}</span></td>
                  <td style={TD}>{c.expires_at ? new Date(c.expires_at).toLocaleDateString() : 'Never'}</td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}

      <PromoCodePanel isOpen={showCreate || !!editing} code={editing} onClose={() => { setShowCreate(false); setEditing(null); }}
        onSave={async (data) => {
          if (!business?.id || !user?.id) return;
          if (editing) {
            await supabase.from('promo_codes').update({ ...data, updated_at: new Date().toISOString() }).eq('id', editing.id);
            setToast({ msg: 'Code updated', type: 'success' }); setEditing(null);
          } else {
            await supabase.from('promo_codes').insert({ ...data, business_id: business.id });
            await supabase.from('activity_log').insert({ business_id: business.id, entity_type: 'promo_code', activity_type: 'promo_created', description: `Promo code "${data.code}" created`, logged_by: user.id });
            setToast({ msg: 'Code created', type: 'success' }); setShowCreate(false);
          }
          void fetchCodes();
        }}
        onDelete={editing ? async () => {
          await supabase.from('promo_codes').update({ is_active: false }).eq('id', editing.id);
          setToast({ msg: 'Code deactivated', type: 'success' }); setEditing(null); void fetchCodes();
        } : undefined}
      />
      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </SettingsSubPage>
  );
}

// ── Create/Edit Panel ──
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface PanelProps { isOpen: boolean; code: PromoCode | null; onClose: () => void; onSave: (data: Record<string, any>) => void; onDelete?: () => void; }

function PromoCodePanel({ isOpen, code, onClose, onSave, onDelete }: PanelProps) {
  const [codeVal, setCodeVal] = useState(''); const [desc, setDesc] = useState('');
  const [discType, setDiscType] = useState<'percentage' | 'fixed'>('percentage');
  const [discVal, setDiscVal] = useState(''); const [minOrder, setMinOrder] = useState('');
  const [maxDisc, setMaxDisc] = useState(''); const [maxUses, setMaxUses] = useState('');
  const [perClient, setPerClient] = useState('1'); const [expiresAt, setExpiresAt] = useState('');
  const [newOnly, setNewOnly] = useState(false); const [active, setActive] = useState(true);
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (isOpen && code) {
      setCodeVal(code.code); setDesc(code.description ?? ''); setDiscType(code.discount_type);
      // discVal is money when type=fixed; raw % number when type=percentage. Canonicalize only when money.
      setDiscVal(code.discount_type === 'fixed' ? canonicalMoney(code.discount_value) : String(code.discount_value));
      setMinOrder(code.min_order_amount ? canonicalMoney(code.min_order_amount) : '');
      setMaxDisc(code.max_discount_amount ? canonicalMoney(code.max_discount_amount) : '');
      setMaxUses(code.max_uses ? String(code.max_uses) : ''); setPerClient(String(code.max_uses_per_client));
      setExpiresAt(code.expires_at ? code.expires_at.split('T')[0] : '');
      setNewOnly(code.new_clients_only); setActive(code.is_active);
    } else if (isOpen) {
      setCodeVal(''); setDesc(''); setDiscType('percentage'); setDiscVal(''); setMinOrder('');
      setMaxDisc(''); setMaxUses(''); setPerClient('1'); setExpiresAt(''); setNewOnly(false); setActive(true);
    }
  }, [isOpen, code]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const F: React.CSSProperties = { marginBottom: 14 };

  const canSave = !!codeVal && !!discVal;

  const submit = () => {
    if (!canSave) return;
    onSave({
      code: codeVal.toUpperCase(), description: desc || null, discount_type: discType,
      discount_value: Number(discVal) || 0,
      min_order_amount: minOrder ? Number(minOrder) : null,
      max_discount_amount: maxDisc ? Number(maxDisc) : null,
      max_uses: maxUses ? Number(maxUses) : null,
      max_uses_per_client: Number(perClient) || 1,
      expires_at: expiresAt ? new Date(expiresAt + 'T23:59:59').toISOString() : null,
      new_clients_only: newOnly, is_active: active,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (e.key === 'Enter' && !(target instanceof HTMLTextAreaElement) && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      if (canSave) { e.preventDefault(); submit(); }
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} title={code ? 'Edit Code' : 'New Promo Code'} subtitle={code ? code.code : 'Create a discount code'}>
      <div onKeyDown={handleKeyDown}>
      <div style={F}><label style={LABEL}>Code *</label><input style={{ ...INPUT, textTransform: 'uppercase', fontFamily: 'monospace' }} value={codeVal} onChange={e => setCodeVal(e.target.value.toUpperCase().replace(/\s/g, ''))} placeholder="WELCOME10" /></div>
      <div style={F}><label style={LABEL}>Internal description</label><input style={INPUT} value={desc} onChange={e => setDesc(e.target.value)} placeholder="10% off for new clients" /></div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <div style={{ flex: 1 }}><label style={LABEL}>Type</label><select style={INPUT} value={discType} onChange={e => setDiscType(e.target.value as any)}><option value="percentage">Percentage (%)</option><option value="fixed">Fixed ($)</option></select></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Value *</label>
          {discType === 'fixed'
            ? <MoneyInput value={discVal} onChange={setDiscVal} />
            : <input type="number" style={INPUT} value={discVal} onChange={e => setDiscVal(e.target.value)} placeholder="10" />
          }
        </div>
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Min order</label><MoneyInput value={minOrder} onChange={setMinOrder} placeholder="Optional" /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Max discount</label><MoneyInput value={maxDisc} onChange={setMaxDisc} placeholder="Optional" /></div>
      </div>
      <div style={{ display: 'flex', gap: 12, ...F }}>
        <div style={{ flex: 1 }}><label style={LABEL}>Total uses limit</label><input type="number" style={INPUT} value={maxUses} onChange={e => setMaxUses(e.target.value)} placeholder="Unlimited" /></div>
        <div style={{ flex: 1 }}><label style={LABEL}>Per-client limit</label><input type="number" style={INPUT} value={perClient} onChange={e => setPerClient(e.target.value)} /></div>
      </div>
      <div style={F}><label style={LABEL}>Expires on</label>
        <DatePicker value={expiresAt || null} onChange={(v) => setExpiresAt(v ?? '')} minDate={new Date().toISOString().split('T')[0]} />
      </div>
      <div style={{ display: 'flex', gap: 16, ...F }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: palette.g700, cursor: 'pointer' }}>
          <input type="checkbox" checked={newOnly} onChange={e => setNewOnly(e.target.checked)} /> New clients only
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: palette.g700, cursor: 'pointer' }}>
          <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} /> Active
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', paddingTop: 16, borderTop: `1px solid ${palette.g100}` }}>
        <div>{onDelete && (
          <button onClick={() => setConfirmDeactivate(true)} style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '8px 14px', borderRadius: 8,
            border: 'none', background: palette.redWash,
            fontSize: 13, fontWeight: 600, color: palette.red,
            cursor: 'pointer', fontFamily: 'inherit',
          }}>
            <Power size={13} /> Deactivate
          </button>
        )}</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${palette.g200}`, background: palette.white, fontSize: 13.5, fontWeight: 500, color: palette.g600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          {/* Q1.5 boundary: hook accepts numbers; money strings are canonical after MoneyInput blur. */}
          <button onClick={submit} disabled={!canSave} style={{ padding: '8px 22px', borderRadius: 8, border: 'none', background: !canSave ? palette.g300 : palette.accent, color: palette.white, fontSize: 13.5, fontWeight: 600, cursor: !canSave ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}>{code ? 'Update' : 'Create'}</button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDeactivate}
        title={`Deactivate "${code?.code ?? ''}"?`}
        message="Customers can't redeem this code while deactivated. You can reactivate it later. Codes already applied to bookings stay valid."
        confirmLabel="Deactivate"
        confirmVariant="danger"
        onClose={() => setConfirmDeactivate(false)}
        onConfirm={() => {
          setConfirmDeactivate(false);
          if (onDelete) onDelete();
        }}
      />
      </div>
    </SlidePanel>
  );
}
